import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

export type VoteType = 'up' | 'down' | null

interface ToolVotes {
  upvotes: number
  downvotes: number
  userVote: VoteType
}

export function useToolVotes(toolId: string) {
  const { user } = useAuth()
  const [votes, setVotes] = useState<ToolVotes>({
    upvotes: 0,
    downvotes: 0,
    userVote: null,
  })
  const [loading, setLoading] = useState(true)

  // Fetch votes for this tool
  useEffect(() => {
    fetchVotes()
  }, [toolId, user])

  async function fetchVotes() {
    try {
      setLoading(true)

      // Get all votes for this tool
      const { data: allVotes, error: votesError } = await supabase
        .from('tool_votes')
        .select('vote_type')
        .eq('tool_id', toolId)

      if (votesError) throw votesError

      // Count upvotes and downvotes
      const upvotes = allVotes?.filter((v) => v.vote_type === 'up').length || 0
      const downvotes =
        allVotes?.filter((v) => v.vote_type === 'down').length || 0

      // Get user's vote if logged in
      let userVote: VoteType = null
      if (user) {
        const { data: userVoteData } = await supabase
          .from('tool_votes')
          .select('vote_type')
          .eq('tool_id', toolId)
          .eq('user_id', user.id)
          .single()

        if (userVoteData) {
          userVote = userVoteData.vote_type as VoteType
        }
      }

      setVotes({ upvotes, downvotes, userVote })
    } catch (error) {
      console.error('Error fetching votes:', error)
    } finally {
      setLoading(false)
    }
  }

  async function vote(voteType: VoteType) {
    if (!user) {
      throw new Error('Must be logged in to vote')
    }

    try {
      // If user already voted the same way, remove vote
      if (votes.userVote === voteType) {
        const { error } = await supabase
          .from('tool_votes')
          .delete()
          .eq('tool_id', toolId)
          .eq('user_id', user.id)

        if (error) throw error

        // Update local state
        setVotes((prev) => ({
          upvotes:
            voteType === 'up' ? Math.max(0, prev.upvotes - 1) : prev.upvotes,
          downvotes:
            voteType === 'down'
              ? Math.max(0, prev.downvotes - 1)
              : prev.downvotes,
          userVote: null,
        }))
      } else {
        // If user voted differently before, update vote
        // Otherwise, insert new vote
        const { error } = await supabase.from('tool_votes').upsert(
          {
            tool_id: toolId,
            user_id: user.id,
            vote_type: voteType,
          },
          {
            onConflict: 'tool_id,user_id',
          },
        )

        if (error) throw error

        // Update local state
        setVotes((prev) => {
          const wasUp = prev.userVote === 'up'
          const wasDown = prev.userVote === 'down'
          const isUp = voteType === 'up'

          return {
            upvotes: isUp
              ? prev.upvotes + (wasUp ? 0 : 1)
              : Math.max(0, prev.upvotes - (wasUp ? 1 : 0)),
            downvotes: !isUp
              ? prev.downvotes + (wasDown ? 0 : 1)
              : Math.max(0, prev.downvotes - (wasDown ? 1 : 0)),
            userVote: voteType,
          }
        })
      }
    } catch (error) {
      console.error('Error voting:', error)
      throw error
    }
  }

  return {
    votes,
    loading,
    vote,
    refresh: fetchVotes,
  }
}
